import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { MAX_UPLOAD_CHUNK_BYTES, validateFileCollectionMetadata } from '../../lib/integrations/storage/constraints';
import type { FileAttachmentReference, FileCollectionConstraints } from '../../lib/integrations/storage/types';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/** Local rows provide immediate feedback but can never satisfy a file answer. */
export interface CollectionAttachment extends FileAttachmentReference { local?: boolean; error?: string; cancellation?: 'requested' | 'completed' }
/** Shared binary transfer state remains mounted while the thread moves into a call. */
export interface FileCollectionState {
  attachments: CollectionAttachment[];
  busyIds: string[];
  error: string | null;
  upload: (fieldId: string, file: File, constraints: FileCollectionConstraints, resumeId?: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  retry: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  selections: Record<string, string[]>;
  selectFiles: (fieldId: string, ids: string[]) => void;
  completedIds: (fieldId: string) => string[] | null;
}
/** Device files and reservation keys live only for this response in memory. */
interface SelectedFile {
  file: File; constraints: FileCollectionConstraints; fieldId: string;
  attempt: string; reservationStarted: boolean; cancelRequested?: boolean;
}
/** All async work owns a response scope and an effect generation. */
interface TransferScope {
  alive: boolean; loaded: boolean; generation: number; attachments: CollectionAttachment[]; error: string | null; loadError: string | null;
  files: Map<string, SelectedFile>; controllers: Map<string, AbortController>;
  cancelling: Set<string>; selections: Map<string, string[]>;
  active: number; waiting: Array<() => void>;
}
/** Stream bounded chunks; at most three files reserve or transfer concurrently. */
export function useFileCollection(responseId: string | null, responseToken: string | null): FileCollectionState {
  const [, redraw] = useReducer((value: number) => value + 1, 0);
  const scope = useMemo<TransferScope>(() => ({ alive: false, loaded: false, generation: 0, attachments: [], error: null, loadError: null,
    files: new Map(), controllers: new Map(), cancelling: new Set(), selections: new Map(), active: 0, waiting: [],
  // A new response or grant must expose empty state in the very first render.
  }), [responseId, responseToken]);
  /** All endpoints require the same respondent grant as the conversation. */
  const request = useCallback(async (path = '', init: RequestInit = {}) => {
    if (!responseId || !responseToken) throw new Error('This conversation is unavailable.');
    const response = await fetch(`/api/responses/${encodeURIComponent(responseId)}/attachments${path}`, {
      ...init, headers: { Authorization: `Bearer ${responseToken}`, ...init.headers },
    });
    if (!response.ok) throw new Error(response.status === 413 ? 'This file is too large.' : response.status === 401 || response.status === 403
      ? 'This upload is unavailable. Contact the person who shared the link.' : 'The file could not upload. Try again.');
    return response.json() as Promise<{ attachment: FileAttachmentReference; attachments: FileAttachmentReference[] }>;
  }, [responseId, responseToken]);
  /** Replace local identity atomically while retaining the respondent's selection order. */
  const accept = useCallback((attachment: CollectionAttachment, previousId = attachment.id) => {
    const index = scope.attachments.findIndex(item => item.id === previousId);
    if (index < 0) scope.attachments.push(attachment);
    else scope.attachments[index] = attachment;
    scope.attachments = scope.attachments.filter((item, position) => item.id !== attachment.id || position === (index < 0 ? scope.attachments.length - 1 : index));
    for (const [fieldId, ids] of scope.selections) scope.selections.set(fieldId, ids.map(id => id === previousId ? attachment.id : id));
    redraw();
  }, [scope]);
  /** A draft selection never mutates the previously accepted files or their provider receipts. */
  const selectFiles = useCallback((fieldId: string, ids: string[]) => {
    scope.selections.set(fieldId, [...new Set(ids)]); scope.error = null; redraw();
  }, [scope]);
  /** Without a draft, recover uploaded files; explicit edits start from the accepted IDs. */
  const selectedFiles = useCallback((fieldId: string) => scope.attachments.filter(item => item.fieldId === fieldId && item.state !== 'cancelled'
    && (!scope.selections.has(fieldId) || scope.selections.get(fieldId)!.includes(item.id))), [scope]);
  const removeSelection = useCallback((fieldId: string, id: string) => selectFiles(fieldId, selectedFiles(fieldId).map(item => item.id).filter(candidate => candidate !== id)), [selectFiles, selectedFiles]);
  const refresh = useCallback(async () => {
    if (!scope.alive || !responseId || !responseToken) return;
    const generation = scope.generation;
    const snapshot = new Map(scope.attachments.map(item => [item.id, item]));
    try {
      const data = await request();
      if (!scope.alive || generation !== scope.generation) return;
      // A list request begun before selection must not erase new rows or newer progress.
      const incoming = new Map(data.attachments.map(item => [item.id, item]));
      const known = new Set(scope.attachments.map(item => item.id));
      scope.attachments = [...scope.attachments.map(item => !item.local && !item.error && !item.cancellation
        && !scope.controllers.has(item.id) && !scope.cancelling.has(item.id) && snapshot.get(item.id) === item
        ? incoming.get(item.id) ?? item : item), ...data.attachments.filter(item => !known.has(item.id))];
      scope.loaded = true; scope.loadError = null; redraw();
    } catch (failure) {
      if (!scope.alive || generation !== scope.generation) return;
      scope.loadError = 'Files could not load. Try again.'; redraw();
      captureException(failure instanceof Error ? failure : String(failure), 'useFileCollection.refresh', { responseId });
    }
  }, [scope, request, responseId, responseToken]);
  useEffect(() => {
    scope.alive = true;
    scope.generation++;
    void refresh();
    return () => {
      scope.alive = false; scope.generation++;
      scope.controllers.forEach(controller => controller.abort()); scope.controllers.clear();
      scope.waiting.splice(0).forEach(start => start()); scope.files.clear();
    };
  }, [scope, refresh]);

  const upload = useCallback(async (fieldId: string, file: File, constraints: FileCollectionConstraints, resumeId?: string) => {
    if (!scope.alive) return;
    const generation = scope.generation;
    let id = resumeId ?? `local-${crypto.randomUUID()}`;
    if (scope.controllers.has(id) || scope.cancelling.has(id)) return;
    const existing = scope.attachments.find(item => item.id === id);
    if (resumeId && (!existing || existing.state === 'cancelled' || existing.state === 'completed')) return;
    const saved = scope.files.get(id) ?? { file, constraints, fieldId, attempt: crypto.randomUUID(), reservationStarted: false };
    scope.files.set(id, saved);
    if (!existing && scope.selections.has(fieldId)) scope.selections.get(fieldId)!.push(id);
    let attachment: CollectionAttachment = existing ?? { id, fieldId, provider: 'google', name: file.name,
      mimeType: file.type || 'application/octet-stream', size: file.size, state: 'pending', uploadedBytes: 0, local: true };
    accept(attachment);
    let metadata;
    try {
      metadata = validateFileCollectionMetadata({ name: file.name, mimeType: file.type || 'application/octet-stream', size: file.size }, constraints);
      if (existing && (!existing.local || saved.reservationStarted) && (existing.fieldId !== fieldId || existing.name !== metadata.name
        || existing.size !== file.size || existing.mimeType !== metadata.mimeType)) throw new Error('Choose the same file to resume.');
      const others = selectedFiles(fieldId).filter(item => item.id !== id);
      if (attachment.local && !saved.reservationStarted && (others.length >= constraints.maxFiles
        || others.reduce((sum, item) => sum + item.size, file.size) > constraints.maxTotalBytes)) throw new Error('This question has reached its file limit.');
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : 'Check this file.';
      scope.error = message; accept({ ...attachment, state: 'failed', error: message }); return;
    }
    saved.file = file; saved.constraints = constraints;
    attachment = { ...attachment, ...metadata, error: undefined };
    accept(attachment); scope.error = null;
    const controller = new AbortController();
    scope.controllers.set(id, controller); redraw();
    const current = () => scope.alive && scope.generation === generation && !controller.signal.aborted;
    // Reserve a slot synchronously; all batch rows exist before the first async result.
    const acquired = await new Promise<boolean>(resolve => {
      const start = () => {
        if (!current()) { resolve(false); return; }
        scope.active++; resolve(true);
      };
      if (scope.active < 3) start();
      else {
        scope.waiting.push(start);
        controller.signal.addEventListener('abort', () => {
          scope.waiting = scope.waiting.filter(item => item !== start); resolve(false);
        }, { once: true });
      }
    });
    if (!acquired) return;
    try {
      if (!current()) return;
      saved.reservationStarted = true;
      const data = attachment.local
        ? await request('', { method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
          body: JSON.stringify({ fieldId, idempotencyKey: saved.attempt, ...metadata }) })
        : await request(`/${encodeURIComponent(id)}`, { signal: controller.signal });
      if (!current()) return;
      const localId = id;
      attachment = { ...data.attachment, ...(saved.cancelRequested ? { cancellation: 'requested' as const } : {}) };
      id = attachment.id;
      scope.controllers.delete(localId); scope.controllers.set(id, controller);
      scope.files.delete(localId); scope.files.set(id, saved);
      accept(attachment, localId);
      if (saved.cancelRequested) {
        attachment = (await request(`/${encodeURIComponent(id)}`, { method: 'DELETE', signal: controller.signal })).attachment;
        if (!current()) return;
        accept(attachment); removeSelection(fieldId, id); scope.files.delete(id); return;
      }
      if (attachment.state === 'pending' || attachment.state === 'expired') {
        attachment = (await request(`/${encodeURIComponent(id)}`, { method: 'POST', signal: controller.signal,
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'resume' }) })).attachment;
        if (!current()) return;
        accept(attachment);
      }
      while (attachment.state !== 'completed') {
        if (attachment.state !== 'pending' && attachment.state !== 'uploading') throw new Error('This upload has expired. Choose the file again.');
        const offset = attachment.uploadedBytes;
        const end = Math.min(file.size, offset + MAX_UPLOAD_CHUNK_BYTES);
        if (offset >= file.size) throw new Error('The upload needs verification. Try again.');
        const next = (await request(`/${encodeURIComponent(id)}`, { method: 'PUT', signal: controller.signal,
          headers: { 'Content-Type': 'application/octet-stream', 'Content-Range': `bytes ${offset}-${end - 1}/${file.size}` }, body: file.slice(offset, end) })).attachment;
        if (!current()) return;
        if (next.state !== 'completed' && next.uploadedBytes <= offset) throw new Error('The upload paused. Try again.');
        attachment = next; accept(attachment);
      }
      scope.files.delete(id);
    } catch (failure) {
      if (!current()) return;
      const message = failure instanceof Error ? failure.message : 'The file could not upload. Try again.';
      scope.error = message;
      accept({ ...attachment, id, state: 'failed', error: message });
      captureException(failure instanceof Error ? failure : String(failure), 'useFileCollection.upload', { responseId, fieldId, attachmentId: id });
    } finally {
      scope.active--;
      if (scope.controllers.get(id) === controller) scope.controllers.delete(id);
      scope.waiting.shift()?.();
      if (scope.alive && scope.generation === generation) redraw();
    }
  }, [scope, accept, request, responseId, selectedFiles, removeSelection]);
  const cancel = useCallback(async (id: string) => {
    if (!scope.alive || scope.cancelling.has(id)) return;
    const generation = scope.generation;
    const attachment = scope.attachments.find(item => item.id === id);
    if (!attachment) return;
    if (attachment.state === 'completed') { removeSelection(attachment.fieldId, id); return; }
    const saved = scope.files.get(id);
    accept({ ...attachment, cancellation: 'requested' });
    if (attachment.local && saved?.reservationStarted) {
      // Do not abort an uncertain reservation: resolve its stable key, then cancel its server ID.
      saved.cancelRequested = true;
      if (!scope.controllers.has(id)) await upload(saved.fieldId, saved.file, saved.constraints, id);
      return;
    }
    scope.controllers.get(id)?.abort();
    if (attachment.local) {
      scope.controllers.delete(id); scope.files.delete(id); scope.error = null;
      accept({ ...attachment, state: 'cancelled' }); return;
    }
    scope.cancelling.add(id); redraw();
    try {
      const result = await request(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!scope.alive || generation !== scope.generation) return;
      accept(result.attachment);
      removeSelection(attachment.fieldId, id);
      scope.files.delete(id); scope.error = null;
    } catch (failure) {
      if (!scope.alive || generation !== scope.generation) return;
      scope.error = 'This upload could not cancel. Try again.';
      accept({ ...attachment, state: 'failed', cancellation: 'requested', error: scope.error });
      captureException(failure instanceof Error ? failure : String(failure), 'useFileCollection.cancel', { responseId, attachmentId: id });
    } finally {
      scope.cancelling.delete(id);
      if (scope.alive && generation === scope.generation) redraw();
    }
  }, [scope, accept, request, responseId, upload, removeSelection]);
  const retry = useCallback(async (id: string) => {
    if (!scope.alive) return;
    const attachment = scope.attachments.find(item => item.id === id);
    if (attachment?.cancellation === 'completed') {
      scope.error = null; accept({ ...attachment, cancellation: undefined, error: undefined }); return;
    }
    if (attachment?.cancellation === 'requested') { await cancel(id); return; }
    const saved = scope.files.get(id);
    if (saved) await upload(saved.fieldId, saved.file, saved.constraints, id);
    else if (scope.alive) { scope.error = 'Reselect the file to resume.'; redraw(); }
  }, [scope, upload, accept, cancel]);
  const completedIds = useCallback((fieldId: string) => {
    if (!scope.alive || !scope.loaded || scope.loadError) return null;
    const selected = selectedFiles(fieldId);
    if (!selected.length || selected.some(item => item.local || item.state !== 'completed' || item.error
      || scope.controllers.has(item.id) || scope.cancelling.has(item.id))) return null;
    return selected.map(item => item.id);
  }, [scope, selectedFiles]);
  return { attachments: scope.attachments, busyIds: [...scope.controllers.keys(), ...scope.cancelling], error: scope.error ?? scope.loadError,
    selections: Object.fromEntries(scope.selections), selectFiles, upload, cancel, retry, refresh, completedIds };
}
